// @file Vuex Store for showing Confirmation Dialog (e.g. for deleting padlets)
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import type { DispatchableAction, Wall } from '@@/types'
import type { Module } from 'vuex'

enum ConfirmationDialogLayoutVariant {
  Small = 'Small',
  Large = 'Large',
  Drawer = 'Drawer',
}

interface ConfirmationDialogPayload {
  subject?: null | { wall: Wall }
  layoutVariant?: ConfirmationDialogLayoutVariant
  iconSrc?: string | null
  iconAlt?: string | null
  title?: string
  body?: string
  customBodyClasses?: string
  confirmButtonText?: string
  cancelButtonText?: string
  afterConfirmActions: Array<DispatchableAction | (() => void)>
  afterDiscardActions?: Array<DispatchableAction | (() => void)>
  afterCancelActions?: Array<DispatchableAction | (() => void)>
  isCodeProtected?: boolean
  buttonScheme: OzConfirmationDialogBoxButtonScheme
  forceFullWidthButtons?: boolean
  xShadow?: boolean
  shouldFadeIn?: boolean
}

interface ConfirmationDialogState extends Required<ConfirmationDialogPayload> {
  isOpen: boolean
}

const blankState = (): ConfirmationDialogState => ({
  isOpen: false,

  layoutVariant: ConfirmationDialogLayoutVariant.Small,
  subject: null,
  iconSrc: null,
  iconAlt: null,
  title: '',
  body: '',
  customBodyClasses: '',
  confirmButtonText: '',
  cancelButtonText: '',
  afterConfirmActions: [],
  afterDiscardActions: [],
  afterCancelActions: [],
  isCodeProtected: false,
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Default,
  forceFullWidthButtons: false,
  xShadow: false,
  shouldFadeIn: true,
})

const ConfirmationDialogModule: Module<ConfirmationDialogState, object> = {
  namespaced: true,
  state: blankState,
  mutations: {
    RESET_CONFIRMATION_DIALOG(state): void {
      Object.assign(state, blankState())
    },
    OPEN_CONFIRMATION_DIALOG(state, dialogState: Partial<ConfirmationDialogState>): void {
      Object.assign(state, dialogState)
      state.isOpen = true
    },
  },
  actions: {
    openConfirmationDialog({ commit }, params: ConfirmationDialogPayload): void {
      commit('OPEN_CONFIRMATION_DIALOG', params)
    },
    executeActions({ dispatch }, actions?: Array<DispatchableAction | (() => void)>): void {
      if (actions === undefined) return

      actions.forEach((action): void => {
        if (typeof action === 'function') {
          action()
        } else {
          void dispatch(action.type, action.payload, { root: true })
        }
      })
    },
    confirm({ commit, dispatch, state }): void {
      void dispatch('executeActions', state.afterConfirmActions)
      commit('RESET_CONFIRMATION_DIALOG')
    },
    cancel({ commit, dispatch, state }): void {
      void dispatch('executeActions', state.afterCancelActions)
      commit('RESET_CONFIRMATION_DIALOG')
    },
    discard({ commit, dispatch, state }): void {
      void dispatch('executeActions', state.afterDiscardActions)
      commit('RESET_CONFIRMATION_DIALOG')
    },
  },
}

export default ConfirmationDialogModule
export { ConfirmationDialogLayoutVariant, OzConfirmationDialogBoxButtonScheme }
export type { ConfirmationDialogPayload, ConfirmationDialogState }
